import React from "react";
import { observer } from "mobx-react";
import copilotStore from "Stores/copilotStore";

const KIND_LABELS = {
  seller_profile: "Seller profile",
  inbox: "Inbox",
  account_list: "Account list",
  contact_list: "Contact list"
};

// camelCase / snake_case key → readable label (mirror of the agent side).
const labelize = key =>
  String(key)
    .replace(/_/g, " ")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/\b\w/g, c => c.toUpperCase())
    .trim();

const formatValue = value => {
  if (value == null || value === "") return null;
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "number") {
    if (value > 1000000000000 && value < 3000000000000) {
      return new Date(value).toLocaleString();
    }
    return String(value);
  }
  if (Array.isArray(value)) {
    const rendered = value
      .map(entry => (typeof entry === "object" ? null : String(entry)))
      .filter(Boolean);
    return rendered.length ? rendered.join(", ") : null;
  }
  if (typeof value === "object") return null;
  return String(value);
};

/**
 * Generic key-value renderer for whatever record the entity card carried —
 * warming status, limits, signature, connected sequences: if the tool
 * returned it, it shows here, without this panel knowing any schema.
 * Nested objects render as their own titled section, one level deep.
 */
const DetailRows = ({ record }) => {
  const rows = [];
  const sections = [];
  Object.entries(record || {}).forEach(([key, value]) => {
    if (key === "_class" || key === "_id") return;
    if (value && typeof value === "object" && !Array.isArray(value)) {
      sections.push([key, value]);
      return;
    }
    const rendered = formatValue(value);
    if (rendered !== null) rows.push([key, rendered]);
  });

  return (
    <>
      <div className="copilotEntityDetail__rows">
        {rows.map(([key, rendered]) => (
          <div key={key} className="copilotEntityDetail__row">
            <span className="copilotEntityDetail__rowLabel">
              {labelize(key)}
            </span>
            <span className="copilotEntityDetail__rowValue">{rendered}</span>
          </div>
        ))}
      </div>
      {sections.map(([key, value]) => (
        <div key={key} className="copilotEntityDetail__section">
          <div className="copilotEntityDetail__sectionTitle">
            {labelize(key)}
          </div>
          <div className="copilotEntityDetail__rows">
            {Object.entries(value).map(([childKey, childValue]) => {
              const rendered = formatValue(childValue);
              if (rendered === null) return null;
              return (
                <div key={childKey} className="copilotEntityDetail__row">
                  <span className="copilotEntityDetail__rowLabel">
                    {labelize(childKey)}
                  </span>
                  <span className="copilotEntityDetail__rowValue">
                    {rendered}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </>
  );
};

const EntityDetailPanel = observer(() => {
  const active = copilotStore.activeEntityItem;
  if (!active) return null;
  const { kind, item } = active;

  return (
    <div className="copilotTablePanel copilotEntityDetail">
      <div className="copilotTablePanel__header">
        <div className="copilotTablePanel__headMeta">
          <div className="copilotTablePanel__title">{item.title}</div>
          <div className="copilotTablePanel__count">
            {KIND_LABELS[kind] || labelize(kind)}
            {item.subtitle ? ` · ${item.subtitle}` : ""}
          </div>
        </div>
        <span
          className="material-icons-outlined copilotTablePanel__close"
          onClick={copilotStore.closeEntityPanel}
        >
          close
        </span>
      </div>
      <div className="copilotTablePanel__body copilotEntityDetail__body">
        <DetailRows record={item.detail} />
      </div>
    </div>
  );
});

export default EntityDetailPanel;
